import type { EventSlots, SlotCandidate, SlotRole } from '../../api/types';
import { PriorityBadge } from '../../components/ui';

/**
 * Les postes d'un événement, modifiables un par un.
 *
 * Choix central : un titulaire n'est pas un libellé suivi d'un bouton, c'est
 * une **liste déroulante dont il est la valeur courante**. Remplacer quelqu'un
 * devient alors le geste le plus simple de l'écran — un seul choix, un seul
 * appel —, et les seules options proposées sont les personnes réellement
 * affectables : le serveur a déjà écarté celles qui ne savent pas, ne sont pas
 * disponibles, sont absentes, saturent une règle ou tiennent ici un rôle
 * qu'elles ne peuvent pas cumuler.
 *
 * Les écartées ne disparaissent pas pour autant : leur motif est consultable.
 * « Untel n'apparaît pas » est la question que se pose un responsable, et
 * l'écran doit y répondre sans qu'il ait à demander.
 */
export default function EventSlotsEditor({
  slots,
  busy,
  onChange,
}: {
  slots: EventSlots;
  busy: boolean;
  /** `person` à `null` libère le poste ; `previous` indique qui s'en va. */
  onChange: (roleId: number, person: number | null, previous: number | null) => void;
}) {
  const roles = slots.roles ?? [];
  if (roles.length === 0) {
    return <p className="muted">Aucun poste à administrer sur cette date.</p>;
  }

  return (
    // Une grille plutôt qu'une colonne : sur un écran large, huit dates à six
    // postes feraient une page de plusieurs mètres, et on perdrait de vue ce
    // qu'on est venu corriger. Sur téléphone, la grille retombe sur une
    // colonne d'elle-même.
    <div className="slot-grid">
      {roles.map((role) => (
        <SlotLine
          key={role.role_id}
          role={role}
          editable={slots.editable}
          busy={busy}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

function SlotLine({
  role,
  editable,
  busy,
  onChange,
}: {
  role: SlotRole;
  editable: boolean;
  busy: boolean;
  onChange: (roleId: number, person: number | null, previous: number | null) => void;
}) {
  const assigned = role.assigned ?? [];
  const candidates = role.candidates ?? [];
  const excluded = role.excluded ?? [];
  const missing = Math.max(0, role.minimum - assigned.length);

  return (
    <div className="slot-line">
      <div className="spread" style={{ alignItems: 'baseline' }}>
        <strong>{role.role_name}</strong>
        <span className="row" style={{ gap: 6, alignItems: 'center' }}>
          <PriorityBadge priority={role.priority} />
          <span className="muted">
            {assigned.length}/{role.target}
            {role.maximum !== role.target ? ` (max ${role.maximum})` : ''}
          </span>
        </span>
      </div>

      {missing > 0 && (
        <p className="warn-text">
          {missing === 1 ? 'Un poste obligatoire reste à pourvoir.' : `${missing} postes obligatoires restent à pourvoir.`}
        </p>
      )}

      {assigned.map((person) => (
        <PersonPicker
          key={person.assignment_id}
          value={person.person_id}
          valueLabel={person.name}
          locked={person.is_locked}
          manual={person.origin === 'MANUAL'}
          candidates={candidates}
          disabled={!editable || busy}
          allowEmpty
          onPick={(next) => onChange(role.role_id, next, person.person_id)}
        />
      ))}

      {editable && assigned.length < role.maximum && (
        <PersonPicker
          value={null}
          valueLabel=""
          candidates={candidates}
          disabled={busy}
          onPick={(next) => onChange(role.role_id, next, null)}
        />
      )}

      {excluded.length > 0 && (
        <details>
          <summary className="muted">
            {/* Formulé comme la question que se pose le responsable, et non
                comme un constat : « pourquoi untel n'est-il pas dans la
                liste ? » est exactement ce qu'il cherche ici. */}
            {excluded.length === 1
              ? 'Pourquoi une personne n’est pas proposée'
              : `Pourquoi ${excluded.length} personnes ne sont pas proposées`}
          </summary>
          <ul className="reasons">
            {excluded.map((item) => (
              <li key={item.person_id}>
                <strong>{item.name}</strong> — {item.detail}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

const SKILL: Record<string, string> = {
  BEGINNER: 'débutant',
  CONFIRMED: 'confirmé',
  EXPERT: 'expert',
};

/**
 * Ce qu'il faut savoir d'un candidat **avant** de le choisir.
 *
 * Le nombre de services déjà pris sur la période est l'information qui évite
 * de recharger involontairement quelqu'un qui sert déjà beaucoup : le solveur
 * répartit, une correction à la main peut défaire cette répartition sans s'en
 * apercevoir. Une réticence déclarée est dite, parce qu'un responsable peut
 * décider de passer outre — mais pas sans le savoir.
 */
function candidateLabel(candidate: SlotCandidate): string {
  const parts = [`${candidate.services_in_run} service${candidate.services_in_run === 1 ? '' : 's'}`];
  const skill = candidate.skill_level ? SKILL[candidate.skill_level] : undefined;
  if (skill) parts.push(skill);
  if (candidate.preference_level === 'AVOID') parts.push('préfère éviter');
  else if (candidate.preference_level === 'HIGH') parts.push('volontaire');
  return `${candidate.name} — ${parts.join(' · ')}`;
}

function PersonPicker({
  value,
  valueLabel,
  locked = false,
  manual = false,
  candidates,
  disabled,
  allowEmpty = false,
  onPick,
}: {
  value: number | null;
  valueLabel: string;
  locked?: boolean;
  manual?: boolean;
  candidates: SlotCandidate[];
  disabled: boolean;
  allowEmpty?: boolean;
  onPick: (person: number | null) => void;
}) {
  // Pas d'état local « en cours » : le parent passe `busy` dès le début de
  // l'appel, et un état local survivrait au remontage des lignes après
  // rechargement, laissant une liste grisée sans raison.
  const handle = (raw: string) => {
    const next = raw === '' ? null : Number(raw);
    if (next !== value) onPick(next);
  };

  return (
    <div className="picker">
      <select
        value={value === null ? '' : String(value)}
        disabled={disabled}
        aria-label={value === null ? 'Ajouter quelqu’un sur ce poste' : `Qui tient ce poste (actuellement ${valueLabel})`}
        onChange={(event) => handle(event.target.value)}
      >
        {value === null ? (
          <option value="">Ajouter quelqu’un…</option>
        ) : (
          <>
            <option value={String(value)}>{valueLabel}</option>
            {allowEmpty && <option value="">— libérer le poste —</option>}
          </>
        )}
        {candidates.map((candidate) => (
          <option key={candidate.person_id} value={String(candidate.person_id)}>
            {candidateLabel(candidate)}
          </option>
        ))}
      </select>
      {value !== null && (locked || manual) && (
        <span className="muted small-note">
          {manual ? 'choix manuel' : 'figé'}
          {locked && manual ? ' et figé' : ''} : une régénération le conservera
        </span>
      )}
    </div>
  );
}
